import React, { useRef, useState } from "react";
import { Button, Form, Offcanvas, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ContactFormModal.css";
import emailjs from "emailjs-com";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import FloatingAlert from "../../components/FlotingAlert/FlotingAlert";

interface ContactFormModalProps {
  onClose: () => void;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({ onClose }) => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [show, setShow] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Floating alert state
  const [floatingAlert, setFloatingAlert] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const newValue = value.replace(/^\s+/, ""); // prevent leading spaces
    setFormData({ ...formData, [name]: newValue });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else {
      const phoneNumber = parsePhoneNumberFromString(formData.mobile);
      if (!phoneNumber || !phoneNumber.isValid()) {
        newErrors.mobile = "Enter a valid phone number with country code";
      }
    }

    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSending(true);

    emailjs
      .sendForm(
        "service_l420l2n",
        "template_i86dqpn",
        formRef.current!,
        "kCRo_PVpsdNP1FPnP"
      )
      .then(() => {
        setFormData({
          name: "",
          email: "",
          mobile: "",
          subject: "",
          message: "",
        });
        setFloatingAlert({ message: "✅ E-mail sent successfully!", type: "success" });
      })
      .catch(() => {
        setFloatingAlert({ message: "❌ Failed to send. Please try again.", type: "error" });
      })
      .finally(() => setIsSending(false));
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          setShow(false);
          onClose();
        }}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Contact Us</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="formName">
                  <Form.Label>Your Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="formMobile">
                  <Form.Label>Mobile Number</Form.Label>
                  <PhoneInput
                    name="mobile"
                    international
                    defaultCountry="IN"
                    value={formData.mobile}
                    onChange={(value) =>
                      setFormData({ ...formData, mobile: value || "" })
                    }
                    className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                  />
                  {errors.mobile && (
                    <div className="invalid-feedback">{errors.mobile}</div>
                  )}
                </Form.Group>
              </Col>

              <Form.Group className="mb-3" controlId="formSubject">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Enter subject"
                  isInvalid={!!errors.subject}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.subject}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Form.Group className="mb-3" controlId="formMessage">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                placeholder="Your message"
                isInvalid={!!errors.message}
                style={{ height: "50px" }} 
              />
              <Form.Control.Feedback type="invalid">
                {errors.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Button variant="success" type="submit" disabled={isSending}>
              {isSending ? "Sending..." : "Send Message"}
            </Button>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Floating Alert */}
      {floatingAlert && (
        <FloatingAlert
          message={floatingAlert.message}
          type={floatingAlert.type}
          duration={3000}
          onClose={() => setFloatingAlert(null)}
        />
      )}
    </>
  );
};

export default ContactFormModal;
